/**
 * Integrytas Group — flatten the nested locale JSON into dot-path string keys.
 *
 * The locale bundles (src/locales/{en,es}.json) are nested objects; the i18n
 * override store (csm_i18n_string) and the management/new-language screens work in
 * flat dot-path keys (e.g. 'users.list.columns.email'). This is the single place
 * that flattens them, so the "every screen prompt" list is derived, never hand-kept.
 */

import en from '../locales/en.json';
import es from '../locales/es.json';
// App-owned screen-prompt extension (§16): derived apps add their OWN keyed
// prompts here (populated by `i18n_extract.mjs --app`) instead of editing the
// inherited en.json, so the management screen lists them and the merge stays clean.
import appEn from '../app/app_locale_en.json';

export function flattenMessages(obj: unknown, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  if (obj && typeof obj === 'object') {
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === 'object') Object.assign(out, flattenMessages(v, key));
      else out[key] = String(v);
    }
  }
  return out;
}

/** The static default prompt for each key, per base bundle. */
export const PROMPT_DEFAULTS: Record<'en' | 'es', Record<string, string>> = {
  en: { ...flattenMessages(en), ...flattenMessages(appEn) },
  es: flattenMessages(es),
};

/** All known screen-prompt keys (union of the base bundles), sorted. */
export const PROMPT_KEYS: string[] = Array.from(
  new Set([...Object.keys(PROMPT_DEFAULTS.en), ...Object.keys(PROMPT_DEFAULTS.es)]),
).sort();

/** The static default for a key in a given CSM locale (falls back to en). */
export function promptDefault(stringKey: string, csmLocale: string): string {
  const base = csmLocale.toLowerCase().startsWith('es') ? 'es' : 'en';
  return PROMPT_DEFAULTS[base][stringKey] ?? PROMPT_DEFAULTS.en[stringKey] ?? '';
}
